import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  OpenInNew,
  Refresh,
  Business,
  Payment,
  Subscriptions,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_STRIPE_ACCOUNT, UPDATE_STORE } from '../graphql/mutations';
import { GET_STRIPE_ACCOUNT_STATUS, GET_STORE_BY_ID } from '../graphql/queries';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CustomSnackbar, { SnackbarMessage } from '../components/common/CustomSnackbar';
import { useState } from 'react';

type ConnectionStatus = 'not_connected' | 'pending' | 'active';

const ACTIVE_SUB_STATUSES = ['active', 'trialing'];

const subStatusConfig = (status: string | undefined) => {
  if (!status || status === 'inactive') return { label: 'Inactive', chipColor: 'default' as const, color: 'text.secondary' };
  if (status === 'active') return { label: 'Active', chipColor: 'success' as const, color: 'success.main' };
  if (status === 'trialing') return { label: 'Trial', chipColor: 'info' as const, color: 'info.main' };
  if (status === 'past_due') return { label: 'Past Due', chipColor: 'warning' as const, color: 'warning.main' };
  if (status === 'canceled') return { label: 'Canceled', chipColor: 'error' as const, color: 'error.main' };
  return { label: status, chipColor: 'default' as const, color: 'text.secondary' };
};

const PaymentSetup = () => {
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const currentStoreId = paramStoreId || authStoreId;

  const [snackbar, setSnackbar] = useState<SnackbarMessage>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: statusData, loading: statusLoading, refetch: refetchStatus } = useQuery(
    GET_STRIPE_ACCOUNT_STATUS,
    {
      variables: { storeId: currentStoreId },
      skip: !currentStoreId,
      fetchPolicy: 'network-only',
    }
  );

  const { data: storeData } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId: currentStoreId },
    skip: !currentStoreId,
  });

  const paymentRequired: boolean = storeData?.storeDetails?.paymentRequired ?? false;
  const platformSubStatus: string | undefined = storeData?.storeDetails?.platformSubscriptionStatus;
  const platformBillingInterval: string | undefined = storeData?.storeDetails?.platformBillingInterval;
  const isSubActive = ACTIVE_SUB_STATUSES.includes(platformSubStatus ?? '');
  const subCfg = subStatusConfig(platformSubStatus);

  const [updateStore, { loading: updatingPaymentRequired }] = useMutation(UPDATE_STORE, {
    onCompleted: (data) => {
      if (data.updateStore.ok) {
        setSnackbar({
          open: true,
          message: data.updateStore.store?.paymentRequired
            ? 'Payment is now required at booking.'
            : 'Payment is now optional at booking.',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: data.updateStore.errors?.join(', ') || 'Failed to update payment setting',
          severity: 'error',
          title: 'Error',
        });
      }
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
        title: 'Error',
      });
    },
  });

  const handlePaymentRequiredToggle = (newValue: boolean) => {
    updateStore({ variables: { storeId: currentStoreId, paymentRequired: newValue } });
  };

  const [createStripeAccount, { loading: onboardingLoading }] = useMutation(CREATE_STRIPE_ACCOUNT, {
    onCompleted: (data) => {
      if (data.createStripeAccount.ok) {
        const url = data.createStripeAccount.result?.onboardingUrl;
        if (url) {
          window.location.href = url;
        } else {
          // Already onboarded — just refresh status
          refetchStatus();
          setSnackbar({
            open: true,
            message: 'Your Stripe account is already connected.',
            severity: 'info',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: data.createStripeAccount.errors?.join(', ') || 'Failed to start onboarding',
          severity: 'error',
          title: 'Error',
        });
      }
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
        title: 'Error',
      });
    },
  });

  const handleStartOnboarding = () => {
    const returnUrl = `${window.location.origin}/stripe/onboarding/return?storeId=${currentStoreId}`;
    const refreshUrl = `${window.location.origin}/stripe/onboarding/refresh?storeId=${currentStoreId}`;
    createStripeAccount({
      variables: {
        storeId: currentStoreId,
        returnUrl,
        refreshUrl,
      },
    });
  };

  if (statusLoading) {
    return <LoadingSpinner message="Loading payment settings..." />;
  }

  const status = statusData?.stripeAccountStatus;

  const connectionStatus: ConnectionStatus = !status
    ? 'not_connected'
    : status.payoutsEnabled && status.chargesEnabled
    ? 'active'
    : 'pending';

  const statusConfig = {
    not_connected: {
      label: 'Not Connected',
      color: theme.palette.text.secondary,
      bgColor: alpha(theme.palette.text.secondary, 0.08),
      icon: <ErrorIcon sx={{ color: theme.palette.text.secondary }} />,
      chipColor: 'default' as const,
    },
    pending: {
      label: 'Pending Verification',
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.08),
      icon: <Warning sx={{ color: theme.palette.warning.main }} />,
      chipColor: 'warning' as const,
    },
    active: {
      label: 'Payouts Enabled',
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.08),
      icon: <CheckCircle sx={{ color: theme.palette.success.main }} />,
      chipColor: 'success' as const,
    },
  };

  const cfg = statusConfig[connectionStatus];

  const onboardingButtonLabel =
    connectionStatus === 'not_connected'
      ? 'Connect Bank Account'
      : connectionStatus === 'pending'
      ? 'Resume Onboarding'
      : 'Update Bank Details';

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: '800px',
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <AccountBalance sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ mb: 0.5 }}>
              Payment Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Connect your bank account to receive payouts
            </Typography>
          </Box>
        </Box>

        {/* Platform Subscription Status Card */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Subscriptions sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" fontWeight={600}>
                Platform Subscription
              </Typography>
            </Box>
            <Chip
              label={subCfg.label}
              color={subCfg.chipColor}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: isSubActive
                ? alpha(theme.palette.success.main, 0.06)
                : alpha(theme.palette.warning.main, 0.06),
              border: 1,
              borderColor: isSubActive ? 'success.main' : 'warning.main',
              mb: 2,
            }}
          >
            {isSubActive ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={500}>
                  Your platform subscription is {platformSubStatus === 'trialing' ? 'in trial' : 'active'}
                  {platformBillingInterval ? ` (${platformBillingInterval})` : ''}.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning sx={{ color: 'warning.main', fontSize: 20 }} />
                <Typography variant="body2" fontWeight={500}>
                  {platformSubStatus === 'past_due'
                    ? 'Payment failed — update your payment method to restore access.'
                    : platformSubStatus === 'canceled'
                    ? 'Subscription cancelled — resubscribe to restore store access.'
                    : 'No active subscription — subscribe to unlock store features.'}
                </Typography>
              </Box>
            )}
          </Box>

          <Button
            variant={isSubActive ? 'outlined' : 'contained'}
            size="small"
            onClick={() => navigate(`/store/${currentStoreId}/subscription`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
          >
            {isSubActive ? 'Manage subscription' : 'Subscribe now'}
          </Button>
        </Paper>

        {/* Stripe Connection Status Card */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              Stripe Connection Status
            </Typography>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={() => refetchStatus()}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2.5,
              borderRadius: 2,
              bgcolor: cfg.bgColor,
              border: 1,
              borderColor: cfg.color,
              mb: 3,
            }}
          >
            {cfg.icon}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: cfg.color }}>
                {cfg.label}
              </Typography>
              {connectionStatus === 'not_connected' && (
                <Typography variant="body2" color="text.secondary">
                  Link your bank account to start receiving customer payments.
                </Typography>
              )}
              {connectionStatus === 'pending' && (
                <Typography variant="body2" color="text.secondary">
                  Stripe needs a bit more information before payouts can be enabled.
                </Typography>
              )}
              {connectionStatus === 'active' && (
                <Typography variant="body2" color="text.secondary">
                  Your account is verified and payouts are active.
                </Typography>
              )}
            </Box>
            <Chip label={cfg.label} color={cfg.chipColor} size="small" sx={{ fontWeight: 600 }} />
          </Box>

          {/* Capability checklist */}
          {status && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Account Capabilities
              </Typography>
              <List disablePadding dense>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {status.chargesEnabled ? (
                      <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    ) : (
                      <Warning sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Charges enabled"
                    secondary={status.chargesEnabled ? 'Ready to accept payments' : 'Verification required'}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {status.payoutsEnabled ? (
                      <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    ) : (
                      <Warning sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Payouts enabled"
                    secondary={status.payoutsEnabled ? 'Funds will be sent to your bank' : 'Bank account verification required'}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {status.detailsSubmitted ? (
                      <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    ) : (
                      <Warning sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Details submitted"
                    secondary={status.detailsSubmitted ? 'Onboarding form complete' : 'Onboarding form not yet submitted'}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>
            </>
          )}

          {/* Outstanding requirements */}
          {status?.requirementsDue && status.requirementsDue.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  border: 1,
                  borderColor: theme.palette.warning.main,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: theme.palette.warning.dark }}>
                  Action required
                </Typography>
                <List disablePadding dense>
                  {status.requirementsDue.map((req: string) => (
                    <ListItem key={req} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Warning sx={{ color: theme.palette.warning.main, fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={req.replace(/_/g, ' ')}
                        primaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}
        </Paper>

        {/* Actions */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            {connectionStatus === 'active' ? 'Manage Bank Account' : 'Get Started'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {connectionStatus === 'active'
              ? 'You can update your bank account details or review your Stripe account at any time.'
              : 'Click below to open Stripe\'s secure onboarding flow. Your bank details are entered directly on Stripe — we never see them.'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                onboardingLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AccountBalance />
                )
              }
              onClick={handleStartOnboarding}
              disabled={onboardingLoading}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: theme.shadows[2],
              }}
            >
              {onboardingLoading ? 'Redirecting to Stripe...' : onboardingButtonLabel}
            </Button>

            {status?.accountId && (
              <Button
                variant="outlined"
                size="large"
                startIcon={<OpenInNew />}
                href={`https://dashboard.stripe.com/b/${status.accountId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Stripe Dashboard
              </Button>
            )}
          </Box>

          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 1.5,
              bgcolor: 'background.default',
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Business sx={{ color: theme.palette.text.secondary, mt: 0.25, fontSize: 20 }} />
            <Typography variant="caption" color="text.secondary">
              HairX uses <strong>Stripe Connect</strong> to securely handle payouts. Your bank details are
              stored by Stripe, not HairX. Stripe is a PCI-compliant payment processor trusted by millions
              of businesses worldwide.
            </Typography>
          </Box>
        </Paper>
        {/* Payment Policy */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper',
            mt: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Payment sx={{ fontSize: 24, color: theme.palette.primary.main, mr: 1.5 }} />
            <Typography variant="h6" fontWeight={600}>
              Payment Policy
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Control whether customers must pay at the time of booking or can pay in person.
          </Typography>

          <Tooltip
            title={connectionStatus !== 'active' ? 'Connect and verify your Stripe account first to enable this setting.' : ''}
            placement="top-start"
          >
            <span>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentRequired}
                    onChange={(e) => handlePaymentRequiredToggle(e.target.checked)}
                    disabled={connectionStatus !== 'active' || updatingPaymentRequired}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Require payment at booking
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {paymentRequired
                        ? 'Customers must pay by card before their booking is confirmed.'
                        : connectionStatus === 'active'
                        ? 'Customers are offered the option to pay now or in person.'
                        : 'Connect Stripe to enable payment collection.'}
                    </Typography>
                  </Box>
                }
              />
            </span>
          </Tooltip>
        </Paper>
      </motion.div>

      <CustomSnackbar
        snackbar={snackbar}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default PaymentSetup;
